/**
 * Razorpay Checkout — lazy script loader + typed Checkout opener.
 *
 * Razorpay ships a JS snippet at https://checkout.razorpay.com/v1/checkout.js
 * that exposes a global `Razorpay` constructor. We load it on demand the
 * first time the user clicks Buy in PackPicker — no point shipping it on
 * every page load when 99% of sessions never visit the payment flow.
 *
 * Security: the script lives on Razorpay's CDN; we never see card data —
 * Checkout iframes their PCI-DSS surface. Our server creates the order;
 * webhook lands the credits. The client only opens the modal.
 */

declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

interface RazorpayOptions {
  key: string;
  amount?: number;         // paise — one-time Orders only
  currency: "INR";
  name: string;            // merchant name shown in modal
  description?: string;
  order_id?: string;
  subscription_id?: string;
  prefill?: {
    email?: string;
    contact?: string;
    name?: string;
  };
  theme?: { color?: string };
  notes?: Record<string, string>;
  handler?: (response: {
    razorpay_payment_id: string;
    razorpay_order_id?: string;
    razorpay_subscription_id?: string;
    razorpay_signature: string;
  }) => void;
  modal?: {
    ondismiss?: () => void;
    confirm_close?: boolean;
  };
}

interface RazorpayInstance {
  open: () => void;
  close: () => void;
}

const CHECKOUT_URL = "https://checkout.razorpay.com/v1/checkout.js";

let _scriptPromise: Promise<void> | null = null;

function loadRazorpayScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Razorpay requires a browser"));
  }
  if (window.Razorpay) return Promise.resolve();
  if (_scriptPromise) return _scriptPromise;

  _scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = CHECKOUT_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      _scriptPromise = null; // allow retry after a network blip
      reject(new Error("Could not load Razorpay Checkout"));
    };
    document.head.appendChild(s);
  });
  return _scriptPromise;
}

export interface OpenCheckoutOpts {
  keyId: string;
  orderId?: string;
  subscriptionId?: string;
  amountPaise?: number;
  packName: string;     // shown as the line item in the modal
  prefillEmail?: string;
  prefillName?: string;
  /** Called when the user closes the modal WITHOUT paying. */
  onDismiss?: () => void;
  /** Called when Razorpay reports a successful capture on the client side.
   *  IMPORTANT: this is NOT the source of truth — credits land on the
   *  webhook. Use this to navigate to /billing/success and let that
   *  screen confirm by reading /me/credits. */
  onSuccess?: (paymentId: string) => void;
}

export async function openRazorpayCheckout(opts: OpenCheckoutOpts): Promise<void> {
  await loadRazorpayScript();
  if (!opts.orderId && !opts.subscriptionId) {
    throw new Error("Checkout requires an order or subscription");
  }
  const rzp = new window.Razorpay({
    key: opts.keyId,
    amount: opts.amountPaise,
    currency: "INR",
    name: "StockSense AI Pro",
    description: opts.packName,
    order_id: opts.orderId,
    subscription_id: opts.subscriptionId,
    prefill: {
      email: opts.prefillEmail,
      name: opts.prefillName,
    },
    theme: { color: "#1a3d2e" },  // matches --brand-deep in the design system
    handler: (resp) => opts.onSuccess?.(resp.razorpay_payment_id),
    modal: {
      ondismiss: () => opts.onDismiss?.(),
      confirm_close: true,        // confirm before letting user kill the flow mid-pay
    },
  });
  rzp.open();
}
